/**
 * City search via Mapbox Geocoding API.
 * Returns structured places (city-level) with a nice dropdown-ready shape.
 *
 * Docs: https://docs.mapbox.com/api/search/geocoding-v5/
 */

export interface CityResult {
  id: string;
  name: string; // e.g. "Porto"
  context: string; // e.g. "Porto District, Portugal"
  center: [number, number]; // [lng, lat]
  bbox?: [number, number, number, number]; // [minLng, minLat, maxLng, maxLat]
}

interface MapboxFeature {
  id: string;
  text: string;
  place_name: string;
  center: [number, number];
  bbox?: [number, number, number, number];
  context?: Array<{ id: string; text: string }>;
}

interface MapboxGeocodingResponse {
  features: MapboxFeature[];
}

const GEOCODING_URL = "https://api.mapbox.com/geocoding/v5/mapbox.places";
const MAPBOX_PUBLIC_TOKEN = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN || "";

/**
 * Search places suitable for poster framing (cities, localities, districts).
 * Deduped, max 6 results.
 */
export async function searchCities(
  query: string,
  signal?: AbortSignal,
): Promise<CityResult[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];
  if (!MAPBOX_PUBLIC_TOKEN) return [];

  const url = new URL(`${GEOCODING_URL}/${encodeURIComponent(trimmed)}.json`);
  url.searchParams.set("access_token", MAPBOX_PUBLIC_TOKEN);
  url.searchParams.set("types", "place,locality,district");
  url.searchParams.set("limit", "6");
  url.searchParams.set("autocomplete", "true");
  url.searchParams.set("language", "en");

  const res = await fetch(url.toString(), { signal });
  if (!res.ok) throw new Error(`Geocoding ${res.status}`);
  const data = (await res.json()) as MapboxGeocodingResponse;

  return data.features.map((f) => ({
    id: f.id,
    name: f.text,
    // Context is an array of admin levels — join the last few for disambiguation.
    context:
      f.place_name
        .split(", ")
        .slice(1) // drop the leading name (same as `text`)
        .join(", ") || f.text,
    center: f.center,
    bbox: f.bbox,
  }));
}
